import { BTaxCart } from '@/app/modules/btax/services/btax-cart.service';
import { selectBTaxAutoDraftEnabled, selectBTaxBlockMessages, selectBTaxSubscriptionsEnabled, selectBTaxWatchListsEnabled } from '@/app/modules/btax/state/btax-state/btax-state.selectors';
import { selectBTaxAccountAddToOrRemoveFromCartTitle, selectBTaxAccountBusinessAddress, selectBTaxAccountFriendlyTitle, selectBTaxAccountHasBusinessAddress, selectBTaxAccountHasMailingAddress, selectBTaxAccountHasTypesOfBusiness, selectBTaxAccountInCart, selectBTaxAccountIsPayable, selectBTaxAccountMailingAddress, selectBTaxAccountTypesOfBusiness } from '@/app/modules/btax/state/entities/account/btax-account.selectors';
import { ChangeDetectionStrategy, Component, forwardRef, HostBinding, Inject, Input } from '@angular/core';
import { AutoDraftService } from '@auto-draft/auto-draft.service';
import { sync } from '@ces/sync';
import { Platform } from '@egovsolutions/angular-platform';
import { Store } from '@ngrx/store';
import { selectUserAccountsEnabled } from '@/app/state/app-state/app-state.selectors';
import { State } from '@state/model';
import { SubscriptionsService } from '@subscriptions/subscriptions.service';
import { WatchListsDialogs } from '@watch-lists/services/watch-lists-dialogs.service';
import { BTaxAccount } from 'egov-api';
import { BehaviorSubject, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Component({
    selector: 'btax-result-item',
    templateUrl: './btax-result-item.component.html',
    styleUrls: ['./btax-result-item.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BusinessResultItemComponent
{
    constructor
    (
        // Dependencies

        private readonly store: Store<State>,
        @Inject(forwardRef(() => SubscriptionsService)) private readonly subscriptions: SubscriptionsService,
        @Inject(forwardRef(() => AutoDraftService)) private readonly autoDraft: AutoDraftService,
        @Inject(forwardRef(() => WatchListsDialogs)) private readonly watchListDialogs: WatchListsDialogs,
        private readonly btaxCart: BTaxCart,


        // Template dependencies

        protected readonly platform: Platform,
    )
    {}


    // Services state

    protected readonly accountsEnabled$ = this.store.select(selectUserAccountsEnabled);
    protected readonly autoDraftEnabled$ = this.store.select(selectBTaxAutoDraftEnabled);
    protected readonly subscriptionsEnabled$ = this.store.select(selectBTaxSubscriptionsEnabled);
    protected readonly watchListsEnabled$ = this.store.select(selectBTaxWatchListsEnabled);


    // Account data

    protected readonly account$ = new BehaviorSubject<BTaxAccount | null>(null);
    @Input() protected set account(value: BTaxAccount | null) { this.account$.next(value); }
    protected get account() { return this.account$.value; }

    protected readonly link$ = this.account$.pipe(
        map(account => account && '/business-tax/account/' + account.accountNumber),
    );

    protected readonly title$ = this.account$.pipe(
        switchMap(account => account
            ? this.store.select(selectBTaxAccountFriendlyTitle(account.accountNumber))
            : of(undefined)
        ),
    );

    protected readonly payable$ = this.account$.pipe(
        switchMap(account => account
            ? this.store.select(selectBTaxAccountIsPayable(account.accountNumber))
            : of(undefined)
        ),
    );

    protected readonly accountInCart$ = this.account$.pipe(
        switchMap(account => account
            ? this.store.select(selectBTaxAccountInCart(account.accountNumber))
            : of(undefined)
        ),
    );

    protected readonly addAccountToOrRemoveFromCartTitle$ = this.account$.pipe(
        switchMap(account => account
            ? this.store.select(selectBTaxAccountAddToOrRemoveFromCartTitle(account.accountNumber))
            : of(undefined)
        ),
    );

    protected readonly hasBusinessAddress$ = this.account$.pipe(
        switchMap(account => account
            ? this.store.select(selectBTaxAccountHasBusinessAddress(account.accountNumber))
            : of(undefined)
        ),
    );

    protected readonly businessAddress$ = this.account$.pipe(
        switchMap(account => account
            ? this.store.select(selectBTaxAccountBusinessAddress(account.accountNumber))
            : of(undefined)
        ),
    );

    protected readonly hasMailingAddress$ = this.account$.pipe(
        switchMap(account => account
            ? this.store.select(selectBTaxAccountHasMailingAddress(account.accountNumber))
            : of(undefined)
        ),
    );

    protected readonly mailingAddress$ = this.account$.pipe(
        switchMap(account => account
            ? this.store.select(selectBTaxAccountMailingAddress(account.accountNumber))
            : of(undefined)
        ),
    );

    protected readonly hasTypesOfBusiness$ = this.account$.pipe(
        switchMap(account => account
            ? this.store.select(selectBTaxAccountHasTypesOfBusiness(account.accountNumber))
            : of(undefined)
        ),
    );

    protected readonly typesOfBusiness$ = this.account$.pipe(
        switchMap(account => account
            ? this.store.select(selectBTaxAccountTypesOfBusiness(account.accountNumber))
            : of(undefined)
        ),
    );

    protected readonly blockMessages$ = this.store.select(selectBTaxBlockMessages);


    // Clickable

    @HostBinding('[routerLink]')
    protected get routerLink() { return this.account && '/business-tax/' + this.account.accountNumber; }

    @HostBinding('class.clickable')
    @HostBinding('class.highlight-on-hover') protected get isTouchDevice() { return sync(this.platform.touchIsMainInput$); }


    // Cart

    protected readonly updatingCartState$ = new BehaviorSubject(false);

    protected toggleAccountInCart(e?: MouseEvent)
    {
        if (e) e.stopPropagation();

        if (!this.updatingCartState$.value)
        {
            this.updatingCartState$.next(true);

            this.btaxCart.toggleAccountInCart(
                this.account!.accountNumber,
                true,
                () => this.toggleAccountInCart(),
            ).subscribe({
                complete: () => this.updatingCartState$.next(false),
                error: () => this.updatingCartState$.next(false),
            });
        }
    }


    // Auto-draft

    protected showAutoDraftDialog(accountNumber: string)
    {
        this.autoDraft.showAutoDraftDialog(accountNumber);
    }


    // Subscriptions

    protected showSubscriptionsDialog(accountNumber: string)
    {
        this.subscriptions.showDialog(accountNumber);
    }


    // Watch lists

    protected watchAccount(accountNumber: string)
    {
        this.watchListDialogs.watchAccount(
        {
            type: 'btax',
            id: accountNumber,
            humanId: sync(this.title$)!,
        });
    }
}
